/**
 * CloudImpostorLayer — the FAR LOD of the mesh-cloud field (2026-09-10,
 * "add a LOD system for clouds so clouds can appear far away").
 *
 * The volumetric field marches real 3D volumes, so it can only afford a few
 * kilometres around the camera; beyond its fade band the sky used to be
 * EMPTY (the field radius was sized for a 4 km far plane the terrain has
 * since pushed to 120 km). This layer continues the SAME weather lattice out
 * to `farRadius` with instanced camera-facing sprites — the Harris (2001)
 * doctrine that impostors suit clouds better than anything else, as
 * flight simulators have done for decades (MSFS 2004: billboards near,
 * impostors far) — every sprite standing for the cloud its cell would grow
 * into: the same cell hash gives the same place, size, character, seed and
 * turret layout (`deriveCloudCell`), so the hand-off between the field's
 * fade-out and the sprite's fade-in swaps two pictures of ONE cloud. Past
 * `midRadius` a coarser lattice (cells × farCellScale) carries clusters to
 * the horizon.
 *
 * The sprite is procedural — no atlas: the cloud's turret lobes projected
 * onto the view plane as soft discs (their spread turns with the viewer's
 * bearing, so a cloud shows a different profile from each side), a slice of
 * the shared 3D noise for the cauliflower texture and edge erosion, a
 * per-pixel normal from the nearest lobe (the "bent normal" billboard trick)
 * for sun wrap lighting, the sky-tinted ambient gradient of the field, a
 * silver lining when the sun stands behind the cloud, Beer-law alpha from
 * the accumulated lobe thickness, and the scene's own exponential fog as
 * aerial perspective so the far clouds sink into the haze the terrain does.
 * Same premultiplied OVER contract as the field, drawn before it
 * (renderOrder) so near volumes composite over far sprites.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../post/mrtOverlay.js';
import {
  Fn, uniform, float, vec2, vec3, vec4, attribute, varying,
  exp, max, min, clamp, dot, mix, normalize, length, smoothstep, texture3D, step, pow, cross,
  cameraPosition, positionLocal, positionPrevious,
} from 'three/tsl';
import {
  CLOUD_ARCHETYPES, coverageThreshold, deriveCloudCell, sampleWeatherCpu,
  type CloudMeshField, type CloudLayerSpec, type CloudArchetype, type CloudImpostorLayerConfig,
} from './CloudMeshField.js';
import { MAX_LOBES } from './CloudLobes.js';

export interface ImpostorCell {
  key: string;
  cx: number; cz: number;
  cellSize: number;
  /** 0 = the field's lattice (mid band), 1 = the coarse far lattice. */
  band: 0 | 1;
  dist: number;
  spec: CloudLayerSpec;
  arch: CloudArchetype;
  layerIndex: number;
}

/**
 * Cells that should carry a sprite (pure, unit-tested): per étage, the
 * field's lattice from `innerRadius` (the field's fade start) to
 * `midRadius`, then the coarse lattice to `farRadius`, each passing the
 * layer's coverage threshold on the weather map. Sorted FAR-FIRST (the draw
 * order — an instanced mesh cannot depth-sort its instances), capped by
 * dropping the farthest.
 */
export function computeImpostorCells(
  camX: number, camZ: number, coverage: number, formation: CloudLayerSpec[],
  opts: { innerRadius: number; midRadius: number; farRadius: number; cellSize: number; farCellScale: number; maxSprites: number; weatherScale: number },
  weather: { data: Uint8Array; size: number },
): ImpostorCell[] {
  const out: ImpostorCell[] = [];
  formation.forEach((spec, li) => {
    const arch = CLOUD_ARCHETYPES[spec.kind];
    const layerCov = Math.max(0, Math.min(1, coverage * spec.coverage));
    if (layerCov < 0.02) return;
    const threshold = coverageThreshold(layerCov);
    const off = { x: li * 3777.7, z: li * -2444.3 }; // the field's per-étage decorrelation
    const bands: Array<{ band: 0 | 1; cell: number; r0: number; r1: number }> = [
      { band: 0, cell: opts.cellSize * arch.cellScale, r0: opts.innerRadius, r1: opts.midRadius * 1.08 },
      { band: 1, cell: opts.cellSize * arch.cellScale * opts.farCellScale, r0: opts.midRadius * 0.92, r1: opts.farRadius },
    ];
    for (const b of bands) {
      const ccx = Math.floor(camX / b.cell), ccz = Math.floor(camZ / b.cell);
      const range = Math.ceil(b.r1 / b.cell);
      for (let dx = -range; dx <= range; dx++) {
        for (let dz = -range; dz <= range; dz++) {
          const cx = ccx + dx, cz = ccz + dz;
          const wx = (cx + 0.5) * b.cell, wz = (cz + 0.5) * b.cell;
          const dist = Math.hypot(wx - camX, wz - camZ);
          if (dist < b.r0 || dist > b.r1) continue;
          const w = sampleWeatherCpu(weather.data, weather.size, wx + off.x, wz + off.z, opts.weatherScale);
          if (w.r < threshold) continue;
          out.push({ key: `I${b.band}L${li}:${cx},${cz}`, cx, cz, cellSize: b.cell, band: b.band, dist, spec, arch, layerIndex: li });
        }
      }
    }
  });
  out.sort((a, b) => b.dist - a.dist); // far first
  if (out.length > opts.maxSprites) out.splice(0, out.length - opts.maxSprites); // drop the farthest
  return out;
}

export class CloudImpostorLayer {
  readonly group = new THREE.Group();
  private readonly _field: CloudMeshField;
  private readonly _cfg: Required<CloudImpostorLayerConfig>;
  private readonly _mesh: THREE.InstancedMesh;
  /**
   * Per-instance data, ALL IN ONE interleaved buffer.
   *
   * WHY (2026-09-15): these were ten separate `InstancedBufferAttribute`s —
   * iCenter, iShape, iSeed and seven lobes. WebGPU allows 8 VERTEX BUFFERS
   * (`maxVertexBuffers`) and three binds one per attribute the shader reads, so
   * with position/normal/uv and the instance matrix this layer asked for 14 and
   * `createRenderPipeline` refused it outright:
   *
   *     Vertex buffer count (13) exceeds the maximum number of vertex buffers (8)
   *
   * The pipeline then stayed invalid forever, and because an invalid pipeline
   * poisons the command buffer it was encoded into, EVERY frame's submit failed
   * — an unbounded console flood and a renderer doing work it could never
   * present. (WebGL2 counts attribute LOCATIONS, not buffers, which is why this
   * only ever broke on the primary backend.)
   *
   * three keys its vertex-buffer map by the underlying buffer
   * (`WebGPUAttributeUtils.createShaderVertexBuffers`), so interleaved
   * attributes sharing one `InstancedInterleavedBuffer` cost ONE buffer no
   * matter how many of them there are. Ten attributes → one. The layer now
   * binds five: position, normal, uv, this, and the instance matrix.
   */
  private readonly _instanceData: THREE.InstancedInterleavedBuffer;
  private readonly _attrs: Record<string, THREE.InterleavedBufferAttribute>;
  /** Float offset of each vec4 slot within one instance's stride. */
  private readonly _slot: Record<string, number> = {};
  private readonly _uHazeColor = uniform(new THREE.Color(0.72, 0.78, 0.86));
  private readonly _uHazeDensity = uniform(0.00002);
  private readonly _uMidRadius: ReturnType<typeof uniform>;
  private readonly _uFarRadius: ReturnType<typeof uniform>;
  private _enabled = true;
  private _lastCellX = Number.NaN;
  private _lastCellZ = Number.NaN;
  private _lastCov = -1;
  private _lastFormation: CloudLayerSpec[] | null = null;
  private _active = 0;

  constructor(opts: { field: CloudMeshField; config?: CloudImpostorLayerConfig }) {
    this._field = opts.field;
    const c = opts.config ?? {};
    this._cfg = {
      midRadius: c.midRadius ?? 16000,
      farRadius: c.farRadius ?? 60000,
      farCellScale: c.farCellScale ?? 4,
      maxSprites: c.maxSprites ?? 1600,
    };
    this._uMidRadius = uniform(this._cfg.midRadius);
    this._uFarRadius = uniform(this._cfg.farRadius);
    const n = this._cfg.maxSprites;
    const geo = new THREE.PlaneGeometry(2, 2, 1, 1);
    // One buffer, ten vec4 slots per instance — see _instanceData.
    const names = ['iCenter', 'iShape', 'iSeed', ...Array.from({ length: MAX_LOBES }, (_, i) => `iLobe${i}`)];
    const stride = names.length * 4;
    this._instanceData = new THREE.InstancedInterleavedBuffer(new Float32Array(n * stride), stride);
    this._instanceData.setUsage(THREE.DynamicDrawUsage);
    this._attrs = {};
    names.forEach((name, k) => {
      this._slot[name] = k * 4;
      const a = new THREE.InterleavedBufferAttribute(this._instanceData, 4, k * 4);
      geo.setAttribute(name, a);
      this._attrs[name] = a;
    });
    const material = this._buildMaterial();
    this._mesh = new THREE.InstancedMesh(geo, material, n);
    const id = new THREE.Matrix4();
    for (let i = 0; i < n; i++) this._mesh.setMatrixAt(i, id);
    this._mesh.instanceMatrix.needsUpdate = true;
    this._mesh.count = 0;
    this._mesh.frustumCulled = false; // positions are computed in the vertex stage
    this._mesh.renderOrder = 9; // before the volumetric meshes (10): far sprites under near volumes
    this._mesh.name = 'CloudImpostors';
    this.group.add(this._mesh);
    this.group.name = 'CloudImpostorLayer';
  }

  get enabled(): boolean { return this._enabled; }
  setEnabled(on: boolean): void { this._enabled = on; this.group.visible = on; }
  get activeCount(): number { return this._active; }
  get config(): Required<CloudImpostorLayerConfig> { return this._cfg; }

  /** Aerial perspective: the scene fog's colour + FogExp2 density (squared law). */
  setHaze(color: { r: number; g: number; b: number }, density: number): void {
    (this._uHazeColor.value as THREE.Color).setRGB(color.r, color.g, color.b);
    this._uHazeDensity.value = density;
  }

  /** Per-frame drive (render-space cam + scene position, like the field). */
  update(_dt: number, cam: THREE.Vector3, scenePos: THREE.Vector3): void {
    if (!this._enabled) return;
    const camAbsX = cam.x - scenePos.x, camAbsZ = cam.z - scenePos.z;
    const sh = this._field.shared;
    const cellX = Math.floor(camAbsX / sh.cfg.cellSize), cellZ = Math.floor(camAbsZ / sh.cfg.cellSize);
    const covBucket = Math.round(sh.coverage / 0.04);
    if (cellX !== this._lastCellX || cellZ !== this._lastCellZ || covBucket !== this._lastCov || sh.formation !== this._lastFormation) {
      this._lastCellX = cellX; this._lastCellZ = cellZ; this._lastCov = covBucket; this._lastFormation = sh.formation;
      this._restream(camAbsX, camAbsZ);
    }
  }

  private _restream(camAbsX: number, camAbsZ: number): void {
    const sh = this._field.shared;
    const cells = computeImpostorCells(camAbsX, camAbsZ, sh.coverage, sh.formation, {
      innerRadius: sh.cfg.fieldRadius * sh.cfg.fadeStartRatio,
      midRadius: this._cfg.midRadius, farRadius: this._cfg.farRadius,
      cellSize: sh.cfg.cellSize, farCellScale: this._cfg.farCellScale,
      maxSprites: this._cfg.maxSprites, weatherScale: sh.cfg.weatherScale,
    }, sh.weather);
    const data = this._instanceData;
    const arr = data.array as Float32Array;
    const stride = data.stride;
    const slot = this._slot;
    const put = (name: string, i: number, x: number, y: number, z: number, w: number): void => {
      const o = i * stride + slot[name];
      arr[o] = x; arr[o + 1] = y; arr[o + 2] = z; arr[o + 3] = w;
    };
    cells.forEach((c, i) => {
      const cell = deriveCloudCell(c.cx, c.cz, c.cellSize, c.spec, c.arch, sh.weather, sh.cfg.weatherScale);
      // A coarse far cell stands for a cluster: grow the sprite with the lattice.
      const sizeMul = c.band === 1 ? Math.sqrt(this._cfg.farCellScale) * 0.9 : 1;
      put('iCenter', i, cell.absX, cell.shape.baseY, cell.absZ, cell.shape.radius * sizeMul);
      put('iShape', i, cell.shape.height * sizeMul, cell.params.x, cell.params.y, c.band);
      put('iSeed', i, cell.seed.x, cell.seed.y, cell.seed.z, cell.type01);
      for (let l = 0; l < MAX_LOBES; l++) { const lb = cell.lobes[l]; put(`iLobe${l}`, i, lb.x, lb.y, lb.z, lb.r); }
    });
    // ONE buffer to flag now: the rows written are contiguous from 0.
    data.needsUpdate = true;
    data.addUpdateRange(0, cells.length * stride);
    this._mesh.count = cells.length;
    this._active = cells.length;
  }

  dispose(): void {
    (this._mesh.material as THREE.Material).dispose();
    this._mesh.geometry.dispose();
    // geometry.dispose() covers the quad only — the interleaved instance data
    // (iCenter/iShape/iSeed/iLobe*, 1600 rows in ONE buffer) is released by the
    // MESH's dispose event; and the group CloudModule scene.add()ed must leave the
    // scene or every teardown strands an orphan Group (2026-09-12).
    this._mesh.dispose();
    this.group.clear();
    this.group.removeFromParent();
  }

  private _buildMaterial(): THREE.NodeMaterial {
    const sh = this._field.shared;
    const S = sh.uniforms;
    const baseNoiseTex = sh.baseNoise;
    const uWorldOffset = sh.worldOffset, uWind = sh.wind, uNoiseScale = sh.noiseScale;
    const uFadeStart = sh.fadeStart, uFadeEnd = sh.fadeEnd;
    const uMid = this._uMidRadius, uFar = this._uFarRadius;
    const uHazeColor = this._uHazeColor, uHazeDensity = this._uHazeDensity;

    const iCenter = attribute('iCenter', 'vec4');
    const iShape = attribute('iShape', 'vec4');
    const iSeed = attribute('iSeed', 'vec4');
    const lobeAttrs = Array.from({ length: MAX_LOBES }, (_, i) => attribute(`iLobe${i}`, 'vec4'));

    // ── Vertex: a spherical billboard over the cloud's base point, spanning
    // ±1.1·radius across and 0 … 1.15·height up (shape space in the varying).
    const vShape = varying(vec2(0, 0), 'vShape');
    const vRight = varying(vec3(1, 0, 0), 'vRight');
    const vToCam = varying(vec3(0, 0, 1), 'vToCam');
    const vDist = varying(float(0), 'vDist');
    const positionNode = Fn(() => {
      const cen = iCenter.xyz.add(uWorldOffset);
      const toCam = cameraPosition.sub(cen);
      const dist = length(toCam);
      const n = toCam.div(max(dist, float(1)));
      // Right axis = up × n, guarded near the zenith.
      const flatLen = length(vec2(n.x, n.z));
      const rightRaw = vec3(n.z, 0, n.x.negate()).div(max(flatLen, float(1e-4)));
      const right = mix(vec3(1, 0, 0), rightRaw, smoothstep(float(0.02), float(0.12), flatLen));
      const up = cross(n, right);
      const corner = positionLocal.xy;
      const sx = corner.x.mul(1.1), sy = corner.y.mul(0.5).add(0.5).mul(1.15);
      vShape.assign(vec2(sx, sy));
      vRight.assign(right);
      vToCam.assign(n);
      vDist.assign(dist);
      const placed = cen.add(right.mul(sx.mul(iCenter.w))).add(up.mul(sy.mul(iShape.x)));
      // Motion vectors: the previous position is this same billboard (a
      // camera-facing card has no object motion of its own); without it
      // three's default previous — the un-placed quad — read as metres of
      // velocity per frame and the motion blur streaked every distant cloud.
      positionPrevious.assign(placed);
      return placed;
    })();

    // ── Fragment ──
    const colorNode = Fn(() => {
      const q = vShape;
      const right = vRight;
      const n = vToCam;
      const dist = vDist;
      const radius = iCenter.w, height = iShape.x;
      // Turret discs projected on the view plane: u = the lobe's world offset
      // along the billboard's right axis (shape units), v = its height.
      let cover: any = float(0);
      let thick: any = float(0);
      let sdN: any = float(10); // nearest lobe's signed distance in units of its radius
      let nearD: any = float(10);
      let nearC: any = vec3(0, 0.5, 0.5); // (u, v, r) of the nearest lobe
      for (let i = 0; i < MAX_LOBES; i++) {
        const l = lobeAttrs[i];
        const live = step(float(0.001), l.w);
        const lu = l.x.mul(right.x).add(l.z.mul(right.z)); // right.y = 0 by construction
        const lv = l.y;
        const dq = length(q.sub(vec2(lu, lv)));
        const m = float(1).sub(smoothstep(l.w.mul(0.55), l.w, dq)).mul(live);
        cover = max(cover, m);
        thick = thick.add(m);
        sdN = min(sdN, mix(float(10), dq.sub(l.w).div(max(l.w, float(0.05))), live));
        const dEdge = dq.sub(l.w);
        const closer = step(dEdge, nearD).mul(live);
        nearC = mix(nearC, vec3(lu, lv, l.w), closer);
        nearD = mix(nearD, dEdge, closer);
      }
      // Cauliflower texture: a slice of the shared 3D noise at the cloud's
      // own seed, scrolled by the wind like the volumes.
      // Cloud-relative frequency like the field's (period ≈ 0.6 of the radius).
      const freq = uNoiseScale.mul(float(450).div(max(radius, float(80))));
      const uvw = vec3(q.x.mul(radius), q.y.mul(height), iSeed.y.mul(0.1))
        .add(iSeed.xyz).add(uWind).mul(freq);
      const nz = texture3D(baseNoiseTex, uvw);
      const nz2 = texture3D(baseNoiseTex, uvw.mul(2.3).add(vec3(0.37, 0.11, 0.73)));
      const fbm = nz.g.mul(0.625).add(nz.b.mul(0.25)).add(nz.a.mul(0.125));
      const noise = clamp(nz.r.mul(0.7).add(fbm.mul(0.5)), float(0), float(1));
      // THE CAULIFLOWER CARVE, the sprite half (mirrors the field): the
      // nearest disc's signed distance is pushed outward by the same
      // contrast-stretched two-octave floret function, so the outline sits on
      // the disc where the noise peaks and retreats 0.4 r in a trough — a
      // disc merely modulated by noise stayed a disc (smooth balloons at
      // every distance in the first pictures).
      const floret = smoothstep(float(0.33), float(0.67), nz.r.mul(0.62).add(nz2.r.mul(0.38)));
      // 0.4 r on a sprite: a 2D disc has no interior to fall back on — at
      // 0.75 the far sky was torn into flakes (the third pictures).
      const sdC = sdN.add(float(1).sub(floret).mul(0.4));
      const coverC = float(1).sub(smoothstep(float(-0.35), float(0.02), sdC));
      // Density: the carved coverage, a light interior texture, edges eroded
      // harder (the field's wispy-edge / billowy-core split), a flat base cut.
      const erode = float(1).sub(coverC).mul(0.5).mul(iShape.z);
      const baseCut = smoothstep(float(0.005), float(0.08), q.y);
      const d = clamp(coverC.mul(1.15).sub(float(1).sub(noise).mul(0.25)).sub(erode), float(0), float(1)).mul(baseCut);
      const alphaRaw = float(1).sub(exp(d.mul(thick.mul(0.6).add(1)).mul(iShape.y).mul(2.2).negate()));
      // Lighting: bent normal from the nearest lobe's sphere, sun wrap, the
      // sky-tinted ambient gradient, a silver lining when the sun is behind.
      const dq2 = q.sub(nearC.xy);
      const rr = max(nearC.z, float(0.05));
      const zz = max(rr.mul(rr).sub(dot(dq2, dq2)), float(0)).sqrt();
      const nLocal = normalize(vec3(dq2.x, dq2.y, zz.add(rr.mul(0.35))));
      const up = cross(n, right);
      const N = normalize(right.mul(nLocal.x).add(up.mul(nLocal.y)).add(n.mul(nLocal.z)));
      const L = normalize(S.sunDirection);
      const wrap = clamp(dot(N, L).mul(0.5).add(0.5), float(0), float(1));
      const v01 = clamp(q.y, float(0), float(1));
      const ambient = mix(vec3(0.80, 0.90, 1.10).mul(S.ambientBottom), vec3(1.0, 1.0, 1.0).mul(S.ambientTop), v01);
      const sunCol = S.sunColor.mul(S.sunIlluminance);
      const direct = sunCol.mul(float(0.05).add(wrap.mul(0.14)).mul(mix(float(0.6), float(1.0), v01)));
      const behind = clamp(dot(n.negate(), L), float(0), float(1));
      const lining = sunCol.mul(pow(behind, float(3)).mul(float(1).sub(d)).mul(0.22));
      let rgb: any = ambient.add(direct).add(lining);
      // Aerial perspective: the scene's FogExp2 law, so far sprites sink into
      // the same haze as the terrain.
      const hz = float(1).sub(exp(dist.mul(uHazeDensity).pow(2).negate()));
      rgb = mix(rgb, uHazeColor, hz);
      // LOD fades: in over the field's fade band (where its volumes fade
      // out), the mid/far lattice hand-off around midRadius, out at farRadius.
      const nearFade = smoothstep(uFadeStart, uFadeEnd, dist);
      const midT = smoothstep(uMid.mul(0.92), uMid.mul(1.08), dist);
      const bandFade = mix(float(1).sub(midT), midT, iShape.w);
      const farFade = float(1).sub(smoothstep(uFar.mul(0.85), uFar, dist));
      const covFade = smoothstep(float(0.02), float(0.08), S.coverage);
      const alpha = alphaRaw.mul(nearFade).mul(bandFade).mul(farFade).mul(covFade);
      return vec4(rgb.mul(alpha), alpha); // premultiplied
    })();

    const material = new THREE.NodeMaterial();
    material.positionNode = positionNode as never;
    material.colorNode = colorNode as never;
    material.side = THREE.DoubleSide;
    material.transparent = true;
    material.depthWrite = false;
    material.fog = false; // the sprite applies the fog law itself (see haze)
    material.blending = THREE.CustomBlending;
    material.blendSrc = THREE.OneFactor;
    material.blendDst = THREE.OneMinusSrcAlphaFactor;
    markMrtOverlay(material); // a sprite over the horizon leaves the ground's normal alone (AO)
    return material;
  }
}
